import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { FileCopy, ArrowBack } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script_load"));
    document.body.appendChild(s);
  });
}

/**
 * Fluxo ConectHUB: cria conexão rascunho (webhook API Oficial), exibe URL para copiar,
 * carrega embed.js e abre CoExConnect.open; onSuccess atualiza a conexão.
 */
function ConecthubEmbedFlowModal({ open, onClose, onCompleted, onBack }) {
  const [phase, setPhase] = useState("idle");
  const [errorText, setErrorText] = useState("");
  const [embedConfig, setEmbedConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  /** JSON formatado do retorno do ConectHUB quando o PUT /conecthub-complete falha */
  const [payloadRecoveryJson, setPayloadRecoveryJson] = useState(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setErrorText("");
    setEmbedConfig(null);
    setDraft(null);
    setPayloadRecoveryJson(null);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }

    const run = async () => {
      setPhase("loading");
      setErrorText("");
      try {
        const { data: cfg } = await api.get("/companies/conecthub-embed-config");
        const accountId = (cfg.accountId || "").trim();
        const publicUrl = (cfg.publicUrl || "").trim();
        if (!publicUrl || !accountId) {
          setErrorText(i18n.t("conecthubEmbed.errors.masterConfig"));
          setPhase("error");
          return;
        }
        setEmbedConfig(cfg);

        const { data: d } = await api.post("/whatsapp/conecthub-draft");
        setDraft({
          whatsappId: d.whatsappId,
          webhookUrl: d.webhookUrl,
        });
        setPhase("webhook");
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.message ||
          i18n.t("conecthubEmbed.errors.generic");
        setErrorText(msg);
        setPhase("error");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCopy = () => {
    if (!draft?.webhookUrl) return;
    navigator.clipboard.writeText(draft.webhookUrl).then(
      () => toast.success(i18n.t("conecthubEmbed.copied")),
      () => toast.error(i18n.t("conecthubEmbed.copyFail"))
    );
  };

  const handleCopyPayload = () => {
    if (!payloadRecoveryJson) return;
    navigator.clipboard.writeText(payloadRecoveryJson).then(
      () => toast.success(i18n.t("conecthubEmbed.payloadCopied")),
      () => toast.error(i18n.t("conecthubEmbed.copyFail"))
    );
  };

  const handleOpenEmbed = async () => {
    if (!embedConfig || !draft) return;
    setPhase("opening");
    try {
      await loadScriptOnce(embedConfig.embedScriptUrl);
      const CoEx = window.CoExConnect;
      if (!CoEx || typeof CoEx.open !== "function") {
        throw new Error("no_coex");
      }

      CoEx.open({
        accountId: embedConfig.accountId,
        channel: "whatsapp",
        webhookUrl: draft.webhookUrl,
        onSuccess: async (data) => {
          try {
            await api.put(`/whatsapp/${draft.whatsappId}/conecthub-complete`, data);
            toast.success(i18n.t("conecthubEmbed.success"));
            if (onCompleted) onCompleted();
            onClose();
          } catch (err) {
            const msg =
              err.response?.data?.error ||
              err.message ||
              i18n.t("conecthubEmbed.errors.save");
            toast.error(msg);
            try {
              setPayloadRecoveryJson(
                JSON.stringify(data, null, 2)
              );
            } catch {
              setPayloadRecoveryJson(String(data));
            }
            setPhase("payloadRecovery");
          }
        },
        onError: (err) => {
          console.error(err);
          toast.error(i18n.t("conecthubEmbed.errors.embed"));
          setPhase("webhook");
        },
      });
      setPhase("embed");
    } catch (e) {
      toast.error(i18n.t("conecthubEmbed.errors.script"));
      setPhase("webhook");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={payloadRecoveryJson ? "md" : "sm"}
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          {onBack ? (
            <IconButton
              edge="start"
              onClick={onBack}
              aria-label="back"
              style={{ marginRight: 8 }}
            >
              <ArrowBack />
            </IconButton>
          ) : null}
          <span>
            {phase === "payloadRecovery"
              ? i18n.t("conecthubEmbed.payloadRecoveryTitle")
              : i18n.t("conecthubEmbed.title")}
          </span>
        </Box>
      </DialogTitle>
      <DialogContent>
        {phase === "loading" && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        )}

        {phase === "error" && (
          <Typography color="error">{errorText}</Typography>
        )}

        {phase === "webhook" && draft && (
          <Box>
            <Typography variant="body2" gutterBottom>
              {i18n.t("conecthubEmbed.webhookHelp")}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              value={draft.webhookUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={handleCopy} size="small">
                      <FileCopy fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenEmbed}
                disabled={phase === "opening"}
              >
                {phase === "opening" ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  i18n.t("conecthubEmbed.openEmbed")
                )}
              </Button>
            </Box>
          </Box>
        )}

        {phase === "embed" && (
          <Typography variant="body2">
            {i18n.t("conecthubEmbed.embedRunning")}
          </Typography>
        )}

        {phase === "payloadRecovery" && payloadRecoveryJson && (
          <Box>
            <Typography variant="body2" color="error" paragraph>
              {i18n.t("conecthubEmbed.payloadRecoveryHelp")}
            </Typography>
            <TextField
              fullWidth
              multiline
              variant="outlined"
              margin="dense"
              value={payloadRecoveryJson}
              InputProps={{
                readOnly: true,
                style: { fontFamily: "monospace", fontSize: 12 },
              }}
              rows={14}
            />
            <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FileCopy />}
                onClick={handleCopyPayload}
              >
                {i18n.t("conecthubEmbed.copyPayload")}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {i18n.t("conecthubEmbed.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConecthubEmbedFlowModal;
